import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, selectIsAuthenticated, selectUser } from './redux/slices/authSlice';
import { GoogleOAuthProvider } from '@react-oauth/google';

import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Public Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Properties from './pages/Properties';
import PropertyDetail from './pages/PropertyDetail';
import OurAgents from './pages/OurAgents';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import Login from './pages/Login';
import Register from './pages/Register';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProperties from './pages/admin/AdminProperties';
import AdminAgents from './pages/admin/AdminAgents';
import AdminBlogs from './pages/admin/AdminBlogs';
import AdminContacts from './pages/admin/AdminContacts';
import AdminTestimonials from './pages/admin/AdminTestimonials'

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token && userData) {
          try {
            const parsedUser = JSON.parse(userData);
            dispatch(setCredentials({
              user: parsedUser,
              token: token
            }));
          } catch (parseError) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      } finally {
        setAuthInitialized(true);
      }
    };
    initializeAuth();
  }, [dispatch]);

  if (!authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        <Routes>
          {/* PUBLIC ROUTES - NO LOGIN REQUIRED */}
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/about" element={<Layout><About /></Layout>} />
          <Route path="/contact" element={<Layout><Contact /></Layout>} />
          <Route path="/properties" element={<Layout><Properties /></Layout>} />
          <Route path="/properties/:id" element={<Layout><PropertyDetail /></Layout>} />
          <Route path="/agents" element={<Layout><OurAgents /></Layout>} />
          <Route path="/blog" element={<Layout><Blog /></Layout>} />
          <Route path="/blog/:slug" element={<Layout><BlogDetail /></Layout>} />
          
          {/* AUTH ROUTES */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* ADMIN ROUTES */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="properties" element={<AdminProperties />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
          </Route>

          {/* USER DASHBOARD */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <div className="container mx-auto px-4 py-8">
                    <h1 className="text-3xl font-bold mb-4">
                      Welcome, {user?.firstName}!
                    </h1>
                    <p>Your personal dashboard</p>
                  </div>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* 404 PAGE - PUBLIC */}
          <Route 
            path="*" 
            element={
              <Layout>
                <div className="text-center py-20">
                  <h1 className="text-4xl font-bold mb-4">404 - Page Not Found</h1>
                  <a href="/" className="text-blue-600 hover:underline">Go Home</a>
                </div>
              </Layout>
            } 
          />
        </Routes>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
